import { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleDetails, relatedArticles } from '@/mocks/articleData';
import { categoryVideos } from '@/mocks/categoryData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!slug) {
    return <Navigate to="/" replace />;
  }

  const article = articleDetails[slug];
  const related = relatedArticles[slug] || [];

  if (!article) {
    const allVideos = Object.values(categoryVideos).flat();
    const videoArticle = allVideos.find((v) => v.slug === slug);

    if (videoArticle) {
      return (
        <>
          <Navbar />
          <main className="pt-20 md:pt-24 pb-14 md:pb-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link to="/category/gaoxiao" className="hover:text-primary-500 transition-colors">分类浏览</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-700 line-clamp-1">{videoArticle.title}</span>
              </nav>

              <article>
                <div className="relative rounded-2xl overflow-hidden mb-8 h-64 md:h-96">
                  <img
                    src={videoArticle.image}
                    alt={videoArticle.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background-950/60 to-transparent flex items-center justify-center">
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-primary-500/90 flex items-center justify-center cursor-pointer hover:bg-primary-600 transition-colors">
                      <i className="ri-play-fill text-background-50 text-3xl md:text-4xl ml-1"></i>
                    </div>
                  </div>
                </div>

                <h1 className="text-xl md:text-3xl font-bold font-heading text-foreground-950 mb-4">
                  {videoArticle.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400 mb-8 pb-6 border-b border-background-200/70">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i> {videoArticle.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {videoArticle.views} 次播放
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i> {videoArticle.duration}
                  </span>
                </div>

                <div className="prose-custom text-foreground-700 leading-relaxed text-sm md:text-base">
                  <p>{videoArticle.excerpt}</p>
                  <p className="mt-4">快猫视频汇聚了海量优质的{videoArticle.title.includes('搞笑') ? '搞笑' : videoArticle.title.includes('美食') ? '美食' : videoArticle.title.includes('旅游') ? '旅游' : videoArticle.title.includes('音乐') ? '音乐' : videoArticle.title.includes('游戏') ? '游戏' : videoArticle.title.includes('科技') ? '科技' : videoArticle.title.includes('才艺') ? '才艺' : '生活'}类短视频内容。每天有超过10万条新视频上传，AI智能推荐让你轻松发现喜欢的视频。下载快猫视频App，开启你的短视频娱乐之旅！</p>
                </div>
              </article>
            </div>
          </main>
          <Footer />
        </>
      );
    }

    return <Navigate to="/" replace />;
  }

  return (
    <>
      <Navbar />
      <main className="pt-20 md:pt-24 pb-14 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/category/keji" className="hover:text-primary-500 transition-colors">分类浏览</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 line-clamp-1">{article.title}</span>
          </nav>

          <article>
            <div className="mb-6">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                {article.category}
              </span>
              <h1 className="text-xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden mb-10 h-64 md:h-80 lg:h-96">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-custom space-y-5 text-foreground-700 leading-relaxed text-sm md:text-base">
              {article.content.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200/70">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-500 text-xs font-medium"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </article>

          {related.length > 0 && (
            <div className="mt-14 pt-10 border-t border-background-200/70">
              <h3 className="text-lg md:text-xl font-bold font-heading text-foreground-950 mb-6">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                {related.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/article/${item.slug}`}
                    className="group flex gap-4 p-4 rounded-2xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="w-28 h-20 md:w-36 md:h-24 flex-shrink-0 rounded-xl overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs text-primary-600 font-medium">{item.category}</span>
                      <h4 className="text-sm font-semibold text-foreground-950 mt-1 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {item.title}
                      </h4>
                      <p className="text-xs text-foreground-400 mt-1 flex items-center gap-1">
                        <i className="ri-calendar-line"></i> {item.date}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.content[0],
            image: article.image,
            datePublished: article.date,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            publisher: {
              '@type': 'Organization',
              name: '快猫视频',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://www.kuaimaovideo.com/article/${slug}`,
            },
          }),
        }}
      />
    </>
  );
}